<template>
  <div data-test="club-search-bar" class="club-search-bar body--p1--medium">
    <div class="club-search-bar__items">
      <div
        class="club-search-bar__input-box club-search-bar__input-box--width"
      >
        <div class="club-search-bar__input-box-border"></div>
        <Suspense>
          <USInputGoogleGeolocation
            v-model:updateCoordinates="coordinatesProp"
            :api-key="apiKey"
            class="club-search-bar__input-item"
            :loc="loc"
            heading="Location"
            @coordinates="updateCoordinates"
          />
        </Suspense>
      </div>
      <div
        class="club-search-bar__input-box club-search-bar__input-box--width"
      >
        <div class="club-search-bar__input-box-border"></div>
        <Suspense>
          <USInputSelect
            v-model="amenitiesValue"
            class="club-search-bar__advanced-input"
            :options="amenitiesTypeSelect"
            :select-diameter="22"
            :multiselect="true"
            direction="bottom"
          >
            <template #heading>
              <p class="display--d2--semibold club-search-bar__input-heading">
                Amenities
              </p>
            </template>
          </USInputSelect>
        </Suspense>
      </div>
    </div>
    <div class="club-search-bar__lower-items">
      <div class="club-search-bar__button-wrapper">
        <USButton
          :label="buttonLabel"
          class="club-search-bar__button"
          @click="
            $emit('submitClubSearch', {
              lat: coordinatesProp ? coordinatesProp.lat : null,
              lon: coordinatesProp ? coordinatesProp.lon : null,
              loc: coordinatesProp ? coordinatesProp.loc : null,
              amenitiesValue: amenitiesValue || null
            })
          "
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, ref, watch } from 'vue'
import USInputGoogleGeolocation from '@base/components/USInputGoogleGeolocation/USInputGoogleGeolocation.vue'
import USInputSelect from '@base/components/USInputSelect/USInputSelect.vue'
import USButton from '@base/components/USButton/USButton.vue'

interface option {
  label: string
  value: string
}

export default defineComponent({
  name: 'ClubSearchBar',
  components: {
    USInputGoogleGeolocation,
    USInputSelect,
    USButton,
  },
  props: {
    apiKey: {
      type: String,
      default: ''
    },
    amenitiesTypeSelect: {
      type: Array as PropType<option[]>,
      default: () => []
    },
    buttonLabel: {
      type: String,
      default: ''
    },
    lat: {
      type: Number,
      default: () => undefined
    },
    lon: {
      type: Number,
      default: () => undefined
    },
    loc: {
      type: String,
      default: () => undefined
    },
    amenities: {
      type: Array as PropType<string[]>,
      default: () => undefined
    }
  },
  emits: ['submitClubSearch'],

  setup(props, { emit }) {

    const amenitiesValue = ref(props.amenities)
    const appliedFilters = ref<number>(0)
    const locationFilled = ref<number>(0)
    const amenitiesFilled = ref<number>(0)

    const coordinatesProp = ref<{
      lat: number
      lon: number
      loc: string
    } | null>(null)
    if (props.lat !== undefined && props.lon !== undefined && props.loc)
      coordinatesProp.value = {
        lat: props.lat,
        lon: props.lon,
        loc: props.loc
      }

    if (props.lat || props.loc || props.lon) {
      locationFilled.value = 1
    }

    if (amenitiesValue.value?.length) {
      amenitiesFilled.value = 1
    }

    const updateAppliedFilter = () => {
      appliedFilters.value =
        locationFilled.value +
        amenitiesFilled.value
    }

    updateAppliedFilter()

    const updateCoordinates = (value: {
      lat: number
      lon: number
      loc: string
    }) => {
      coordinatesProp.value = value
    }

    watch(
      () => coordinatesProp.value,
      (newValue) => {
        if (newValue?.lat || newValue?.lon || newValue?.loc) {
          locationFilled.value = 1
        } else {
          locationFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    watch(
      () => amenitiesValue.value,
      (newValue) => {
        if (newValue?.length) {
          amenitiesFilled.value = 1
        } else {
          amenitiesFilled.value = 0
        }
        updateAppliedFilter()
      }
    )

    return {
      amenitiesValue,
      updateCoordinates,
      coordinatesProp,
      appliedFilters
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';
@use '@base/styles/v1.0/scss/foundations/motion';

.club-search-bar {
  align-items: center;
  background: var(--color--neutral-a5);
  border-radius: var(--border-radius--large);
  box-shadow: var(--box-shadow--b1);
  display: flex;
  flex-flow: column;
  padding: var(--spacing--6);
  margin-bottom: var(--spacing--5);

  @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
    flex-flow: row nowrap;
  }

  &__items {
    color: var(--color--primary-b2);
    display: flex;
    flex-flow: column;
    width: calc(100% - var(--spacing--6));

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      flex-flow: row nowrap;
    }
  }

  &__input-heading {
    color: var(--color--neutral-a1) !important;
    font-weight: var(--font-weight--medium) !important;
    margin-bottom: var(--spacing--8);
  }

  &__input-box {
    margin-bottom: var(--spacing--7);

    &--width {
      width: 100%;
    }
  }

  &__input-box-border {
    border-right: var(--line-weight--light) solid var(--color--neutral-a4);
    bottom: 0;
    display: none;
    height: 100%;
    position: absolute;
    right: calc(0px + var(--spacing--8));
    width: var(--spacing--8);
    z-index: 2;

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      display: inline-block;
    }
  }

  &__lower-items {
    padding-top: var(--spacing--6);

    @include breakpoint.min-width(breakpoint.$breakpoint--desktop) {
      padding-left: var(--spacing--5);
      padding-top: 0;
    }
  }

  &__icon {
    margin-right: var(--spacing--8);
    transform: rotate(0deg);
    transition: transform motion.duration(3) ease-in;
    will-change: transform;

    &--rotate {
      transform: rotate(45deg);
    }
  }
}

:deep(.us-input) {
  color: var(--color--neutral-a1) !important;
  font-weight: var(--font-weight--medium) !important;
}

:deep(.input-heading-wrapper) {
  color: var(--color--neutral-a1) !important;
  font-weight: var(--font-weight--medium) !important;
  margin-bottom: 0 !important;
}

:deep(.input-wrapper--placeholder) {
  color: var(--color--primary-b3) !important;
  font-weight: var(--font-weight--regular) !important;
}

//Geo
:deep(.us-input-google-geo__input) {
  color: var(--color--primary-b3) !important;
  font-weight: var(--font-weight--regular) !important;
  padding: var(--spacing--8) 0 !important;
  padding-right: var(--spacing--5) !important;
}

:deep(.us-input-google-geo__input::placeholder) {
  color: var(--color--primary-b3) !important;
}

:deep(.us-input-google-geo__current-loc-button-wrapper) {
  @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
    right: var(--spacing--6) !important;
  }
}

//Amenities
:deep(.us-input-select) {
  border: none !important;
  color: var(--color--primary-b3) !important;
  font-weight: var(--font-weight--regular) !important;
  padding: var(--spacing--8) 0 !important;

  .select__selected {
    padding: 0;
  }

  .select__close-icon, .select__open-icon {
    right: var(--spacing--9) !important;
    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      right: var(--spacing--6) !important;
    }
  }
}
</style>
